import { Dispatch, Fragment, SetStateAction, useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Skeleton,
  Typography,
} from '@mui/material';
import { formatDate } from 'utility/helpers';
import HandleOperation from 'components/utils/HandleOperation';
import { styled, useTheme } from '@mui/material/styles';
import Dot from 'components/common/dot/Dot';
import {
  ArticleType,
  useArticlesAndPlaylistsPaginationQuery,
} from 'pages/knowledge/hooks/useArticlesQuery';
import Search from 'pages/knowledge/components/SearchComponent';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import { Link as RouterLink } from 'react-router-dom';
import { USER_PROFILE, VIEW_ARTICLE } from 'constants/routes';
import { Virtuoso } from 'react-virtuoso';
import PaginationLoader from 'components/utils/PaginationLoader';
import SearchLineIcon from 'remixicon-react/SearchLineIcon';
import FileListLineIcon from 'remixicon-react/FileListLineIcon';
import { FeedFilter } from 'api/KnowledgeAPI';

type AddPostsToPlaylistModalProps = {
  excludeArticleIds: string[];
  isSubmitting: boolean;
  open: boolean;
  title: string;
  filters?: FeedFilter[];
  Actions: (options: { selectedArticles: ArticleType[] }) => JSX.Element;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

function SelectArticlesModal({
  excludeArticleIds,
  isSubmitting,
  open,
  title,
  filters,
  Actions,
  setOpen,
}: AddPostsToPlaylistModalProps) {
  const theme = useTheme();
  const [searchKeyword, setSearchKeyword] = useState('');
  const {
    articles,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    searchInfo: { isLoading: searchLoading },
    fetchNextPage,
  } = useArticlesAndPlaylistsPaginationQuery({
    searchKeyword,
    pageNo: 0,
    pageSize: 10,
    searchOnly: !!searchKeyword,
    ...(filters && { filters }),
  });
  const contentRef = useRef<HTMLDivElement | undefined>(undefined);
  const [selectedArticles, setSelectedArticles] = useState<ArticleType[]>([]);

  const articlesData = useMemo(
    () => articles?.filter((article) => !excludeArticleIds.includes(article.id)) || [],
    [articles, excludeArticleIds],
  );

  const handleChange = (articleData: ArticleType) => {
    if (!isSubmitting) {
      setSelectedArticles((prevState) => {
        const selectedIds = [...prevState];
        const index = selectedIds.findIndex((article) => article.id === articleData.id);
        if (index > -1) {
          selectedIds.splice(index, 1);
        } else {
          selectedIds.push(articleData);
        }

        return selectedIds;
      });
    }
  };

  const closeModal = () => {
    setOpen(false);
    setSelectedArticles([]);
    setSearchKeyword('');
  };

  const fetchPosts = useCallback(() => {
    if (!isFetching && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [isFetching, isFetchingNextPage, hasNextPage, fetchNextPage]);

  const isArticleSelected = (articleId: string) =>
    !!selectedArticles?.find((article) => article.id === articleId);

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      onClose={closeModal}
      sx={{ '& .MuiPaper-root': { height: '100%' } }}
    >
      <CustomDialogTitle onClose={closeModal}>
        <Title>{title}</Title>
      </CustomDialogTitle>
      <DialogContent dividers sx={{ p: 0 }} ref={contentRef}>
        <StickySearchContainer>
          <Search
            value={searchKeyword}
            placeholder="Search..."
            setValue={setSearchKeyword}
            filled
            triggerOnEnter
          />
        </StickySearchContainer>

        <HandleOperation<ArticleType>
          isLoading={isLoading || searchLoading}
          data={articlesData}
          noDataComponent={
            <NoDataContainer>
              {searchKeyword ? (
                <>
                  <SearchLineIcon size={120} color={theme.palette.custom.steel} />
                  <NoDataDescription>
                    No results found for <b>"{searchKeyword}"</b>
                  </NoDataDescription>
                </>
              ) : (
                <>
                  <FileListLineIcon size={120} color={theme.palette.custom.steel} />
                  <NoDataDescription>No posts found</NoDataDescription>
                </>
              )}
            </NoDataContainer>
          }
          skelton={
            <ArticleHoverContainer>
              <ArticleContainer sx={{ alignItems: 'flex-start' }}>
                <Skeleton animation="wave" variant="text" width={25} height={40} />
                <Box flex={1} ml={2}>
                  <Skeleton animation="wave" variant="text" width="70%" height={25} />
                  <Box display="flex" alignItems="center">
                    <Skeleton
                      animation="wave"
                      variant="text"
                      width={100}
                      height={20}
                      sx={{ mr: 1 }}
                    />
                    <Skeleton animation="wave" variant="text" width={100} height={20} />
                  </Box>
                </Box>
              </ArticleContainer>
              <Divider />
            </ArticleHoverContainer>
          }
        >
          <Virtuoso
            customScrollParent={contentRef.current}
            data={articlesData}
            endReached={fetchPosts}
            itemContent={(index, item) => (
              <Fragment key={item.id}>
                <ArticleHoverContainer onClick={handleChange.bind(null, item)}>
                  <ArticleContainer>
                    <Checkbox checked={isArticleSelected(item.id)} disabled={isSubmitting} />
                    <Box ml={0.5}>
                      <ArticleTitle>
                        <Link to={VIEW_ARTICLE.replace(':articleId', item.id)} target="_blank">
                          {item.title}
                        </Link>
                      </ArticleTitle>
                      <ArticleInfo>
                        <Link
                          to={USER_PROFILE.replace(':userName', item.createdBy)}
                          target="_blank"
                          sx={{ textTransform: 'capitalize' }}
                        >
                          <span title="Published By">{item.userNameToDisplay}</span>
                        </Link>
                        <Dot />
                        <span title="Last Updated">{formatDate(item.lastUpdatedAt)}</span>
                      </ArticleInfo>
                    </Box>
                  </ArticleContainer>
                  {articlesData.length - 1 !== index && <Divider />}
                </ArticleHoverContainer>
              </Fragment>
            )}
            components={{
              Footer: () => <PaginationLoader isLoading={isFetchingNextPage} />,
            }}
          />
        </HandleOperation>
      </DialogContent>

      <DialogActions>
        <Actions selectedArticles={selectedArticles} />
      </DialogActions>
    </Dialog>
  );
}

const Title = styled(Typography, { label: 'Title' })(({ theme }) => ({
  fontSize: 24,
  fontWeight: 700,
  color: theme.palette.custom.black,
}));

const StickySearchContainer = styled(Box, { label: 'StickySearchContainer' })(({ theme }) => ({
  position: 'sticky',
  top: '0',
  backgroundColor: theme.palette.custom.white,
  zIndex: 1,
  padding: '16px 24px',
}));

const ArticleHoverContainer = styled(Box, { label: 'ArticleHoverContainer' })(({ theme }) => ({
  padding: '0 24px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.custom.whiteSmoke,
    transition: '0.5s',
  },
}));

const ArticleContainer = styled(Box, { label: 'ArticleContainer' })({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  padding: '8px 0',
});

const ArticleTitle = styled(Typography, { label: 'ArticleTitle' })(({ theme }) => ({
  fontWeight: 700,
  fontSize: 16,
  color: theme.palette.custom.tuna,
}));

const ArticleInfo = styled(Typography, { label: 'ArticleInfo' })(({ theme }) => ({
  fontWeight: 400,
  fontSize: 13,
  color: theme.palette.custom.mistBlue,
}));

const Link = styled(RouterLink, { label: 'Link' })(({ theme }) => ({
  '&:hover': {
    cursor: 'pointer',
    color: theme.palette.custom.link,
    textDecoration: 'underline',
  },
}));

const NoDataContainer = styled(Box, { label: 'NoDataContainer' })({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: '60px 24px 0',
});

const NoDataDescription = styled(Box, { label: 'NoDataDescription' })(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.custom.mistBlue,
}));

export default SelectArticlesModal;
